import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from "@/components/payment-form";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { TherapistWithSpecialties } from "@shared/schema";

// Initialize Stripe
const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY || "pk_test_your_test_key";
const stripePromise = loadStripe(stripeKey);

export default function CheckoutPage() {
  const { therapistId, date } = useParams<{ therapistId: string, date: string }>();
  const [, navigate] = useLocation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Parse session date
  const sessionDate = new Date(decodeURIComponent(date));
  
  // Fetch therapist details
  const { data: therapist, isLoading: therapistLoading } = useQuery<TherapistWithSpecialties>({
    queryKey: [`/api/therapists/${therapistId}`],
  });
  
  // Create payment intent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        if (!therapistId || !date) {
          throw new Error("Missing therapist ID or session date");
        }
        
        setIsLoading(true);
        setError(null);
        
        const response = await apiRequest("POST", "/api/create-payment-intent", {
          therapist_id: parseInt(therapistId),
          session_date: sessionDate.toISOString(),
        });
        
        const data = await response.json();
        setClientSecret(data.client_secret);
      } catch (err: any) {
        console.error("Error creating payment intent:", err);
        setError(err.message || "Erro ao processar o pagamento. Tente novamente.");
      } finally {
        setIsLoading(false);
      }
    };
    
    createPaymentIntent();
  }, [therapistId, date]);
  
  // Loading state
  if (therapistLoading || isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow bg-neutral flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
            <p className="text-gray-600">Preparando pagamento...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  // Error state
  if (error || !therapist) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow bg-neutral flex items-center justify-center">
          <Card className="w-full max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-xl text-red-500">Erro no Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                {error || "Não foi possível carregar os dados do terapeuta. Tente novamente."}
              </p>
              <button 
                onClick={() => navigate("/booking")}
                className="bg-primary text-white px-4 py-2 rounded-md"
              >
                Voltar para agendamento
              </button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow bg-neutral">
        <section className="container mx-auto px-4 py-8 md:py-12">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-bold">
                Pagamento de Sessão
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <PaymentForm 
                    therapistName={therapist.name}
                    sessionDate={sessionDate}
                    amount={therapist.hourly_rate}
                  />
                </Elements>
              ) : (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
